import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon } from '@mui/icons-material';
import { fetchPayments } from '../store/slices/paymentsSlice';
import { RootState, AppDispatch } from '../store';
import { Payment } from '../types';
import { PAYMENT_STATUS } from '../constants';

const Payments: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { payments, loading, totalCount, currentPage, pageSize } = useSelector(
    (state: RootState) => state.payments
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    const filters: any = {};
    if (searchTerm) filters.search = searchTerm;
    if (statusFilter) filters.status = statusFilter;

    dispatch(fetchPayments({ 
      page: page + 1, 
      pageSize: rowsPerPage, 
      filters 
    }));
  }, [dispatch, page, rowsPerPage, searchTerm, statusFilter]);

  const handleSearch = () => {
    setPage(0);
    const filters: any = {};
    if (searchTerm) filters.search = searchTerm;
    if (statusFilter) filters.status = statusFilter;

    dispatch(fetchPayments({ 
      page: 1, 
      pageSize: rowsPerPage, 
      filters 
    }));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case PAYMENT_STATUS.COMPLETED:
        return 'success';
      case PAYMENT_STATUS.PENDING:
      case PAYMENT_STATUS.PENDING_CARD_REGISTRATION:
      case PAYMENT_STATUS.CARD_REGISTERED:
      case PAYMENT_STATUS.PROCESSING:
      case PAYMENT_STATUS.RETRY_PENDING:
        return 'warning';
      case PAYMENT_STATUS.FAILED:
        return 'error';
      case PAYMENT_STATUS.CANCELLED:
        return 'default';
      case PAYMENT_STATUS.REFUNDED:
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case PAYMENT_STATUS.COMPLETED:
        return '完了';
      case PAYMENT_STATUS.PENDING:
        return '待機中';
      case PAYMENT_STATUS.PENDING_CARD_REGISTRATION:
        return 'カード登録待ち';
      case PAYMENT_STATUS.CARD_REGISTERED:
        return 'カード登録済み';
      case PAYMENT_STATUS.PROCESSING:
        return '処理中';
      case PAYMENT_STATUS.FAILED:
        return '失敗';
      case PAYMENT_STATUS.CANCELLED:
        return 'キャンセル';
      case PAYMENT_STATUS.REFUNDED:
        return '返金済み';
      case PAYMENT_STATUS.RETRY_PENDING:
        return '再試行待ち';
      default:
        return status;
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        決済管理
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="検索（顧客ID、決済ID）"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>ステータス</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="ステータス"
              >
                <MenuItem value="">すべて</MenuItem>
                <MenuItem value={PAYMENT_STATUS.PENDING}>待機中</MenuItem>
                <MenuItem value={PAYMENT_STATUS.PENDING_CARD_REGISTRATION}>カード登録待ち</MenuItem>
                <MenuItem value={PAYMENT_STATUS.CARD_REGISTERED}>カード登録済み</MenuItem>
                <MenuItem value={PAYMENT_STATUS.PROCESSING}>処理中</MenuItem>
                <MenuItem value={PAYMENT_STATUS.COMPLETED}>完了</MenuItem>
                <MenuItem value={PAYMENT_STATUS.FAILED}>失敗</MenuItem>
                <MenuItem value={PAYMENT_STATUS.CANCELLED}>キャンセル</MenuItem>
                <MenuItem value={PAYMENT_STATUS.REFUNDED}>返金済み</MenuItem>
                <MenuItem value={PAYMENT_STATUS.RETRY_PENDING}>再試行待ち</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
            >
              検索
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={loading}
            >
              新規決済
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>決済ID</TableCell>
                  <TableCell>顧客ID</TableCell>
                  <TableCell>金額</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>説明</TableCell>
                  <TableCell>作成日時</TableCell>
                  <TableCell>更新日時</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (!payments || payments.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (!payments || payments.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      決済データがありません
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment: Payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>{payment.paymentId}</TableCell>
                      <TableCell>{payment.customerId}</TableCell>
                      <TableCell>{formatAmount(payment.amount, payment.currency)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(payment.status)}
                          color={getStatusColor(payment.status) as any}
                          size="small"
                          variant="filled"
                          sx={{ 
                            cursor: 'default',
                            pointerEvents: 'none'
                          }}
                        />
                      </TableCell>
                      <TableCell>{payment.description || '-'}</TableCell>
                      <TableCell>
                        {new Date(payment.createdAt).toLocaleString('ja-JP')}
                      </TableCell>
                      <TableCell>
                        {new Date(payment.updatedAt).toLocaleString('ja-JP')}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} / ${count !== -1 ? count : `${to}以上`}`
            }
          />
        </Paper>
    </Box>
  );
};

export default Payments;